<template>
  <div class="parameters-form">
    <div class="parameters-form__title">
      {{ $t('general.parameters') }}
    </div>
    <s-fieldset
      class="parameters-form__fieldset"
      :legend="fieldsetLegend"
    >
      <b-row>
        <b-col sm="6" >
          <label for="value-to-high">
            {{
              $t('fields.engineering.cooling-efficiency.value-to-high')
            }}<span v-if="editMode">*</span>
          </label>
          <s-input-label-edit
            class="form-group"
            v-model="$v.parameters
              .maximumAllowableDifferenceTemperaturesOilMeasureCalculated.valueToHigh.$model"
            :editable="editMode"
          >
            <s-number-input
              id="value-to-high"
              class="parameters-form__input text-right"
              v-model="$v.parameters
                .maximumAllowableDifferenceTemperaturesOilMeasureCalculated.valueToHigh.$model"
              v-s-trigger:input="validateHighValues"
              :disabled="saving"
              :validation="$v.parameters
                .maximumAllowableDifferenceTemperaturesOilMeasureCalculated.valueToHigh"
              :invalid="$v.parameters
                .maximumAllowableDifferenceTemperaturesOilMeasureCalculated.valueToHigh.$error"
              :numeric-options="highValuesNumericOptions"
            />
            <b-form-invalid-feedback
              v-show="$v.parameters
                .maximumAllowableDifferenceTemperaturesOilMeasureCalculated.valueToHigh.$error"
            >
              {{ getValueToHighValidationMessage() }}
            </b-form-invalid-feedback>
          </s-input-label-edit>
        </b-col>
        <b-col sm="6">
          <label for="value-to-very-high">
            {{
              $t('fields.engineering.cooling-efficiency.value-to-very-high')
            }}<span v-if="editMode">*</span>
          </label>
          <s-input-label-edit
            class="form-group"
            v-model="$v.parameters
              .maximumAllowableDifferenceTemperaturesOilMeasureCalculated.valueToVeryHigh.$model"
            :editable="editMode"
          >
            <s-number-input
              id="value-to-very-high"
              class="parameters-form__input text-right"
              v-model="$v.parameters
                .maximumAllowableDifferenceTemperaturesOilMeasureCalculated.valueToVeryHigh.$model"
              v-s-trigger:input="validateHighValues"
              :disabled="saving"
              :validation="$v.parameters
                .maximumAllowableDifferenceTemperaturesOilMeasureCalculated.valueToVeryHigh"
              :invalid="$v.parameters
                .maximumAllowableDifferenceTemperaturesOilMeasureCalculated.valueToVeryHigh.$error"
              :numeric-options="highValuesNumericOptions"
            />
            <b-form-invalid-feedback
              v-show="$v.parameters
                .maximumAllowableDifferenceTemperaturesOilMeasureCalculated.valueToVeryHigh.$error"
            >
              {{ getValueToVeryHighValidationMessage() }}
            </b-form-invalid-feedback>
          </s-input-label-edit>
        </b-col>
      </b-row>
    </s-fieldset>
    <b-row class="mx-3">
      <b-col class="p-0">
        <label for="diagnostic-emission-timing">
          {{ diagnosticEmissionTimingLabel }}<span v-if="editMode">*</span>
        </label>
        <s-input-label-edit
          v-model="$v.parameters
            .timingDiagnosticEmissionMaximumPermissibleDifferenceExceeded.$model"
          :editable="editMode"
        >
          <b-form-spinbutton
            id="diagnostic-emission-timing"
            class="parameters-form__input"
            v-model="$v.parameters
              .timingDiagnosticEmissionMaximumPermissibleDifferenceExceeded.$model"
            type="number"
            :state="inputOptions.timingDiagnosticEmissionMaximumPermissibleDifferenceExceeded.state"
            :step="parameters.algorithmExecutionCycleTime"
            :number="true"
            :min="0"
            :max="300"
            :disabled="saving"
          />
          <b-form-invalid-feedback
            v-if="$v.parameters
              .timingDiagnosticEmissionMaximumPermissibleDifferenceExceeded.$invalid"
          >
            <div v-html="getRangeValidationTimingDiagnosticEmission()">
            </div>
            <div v-html="getMultipleValidationTimingDiagnosticEmission()">
            </div>
          </b-form-invalid-feedback>
        </s-input-label-edit>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { getOperatorLabel } from '@/services/stringHelper';
import { required, minValue, maxValue } from 'vuelidate/lib/validators';

export default {
  name: 'ParametersForm',
  props: {
    parameters: {
      type: Object,
      default: () => ({}),
    },
    saving: {
      type: Boolean,
      default: false,
    },
    editMode: {
      type: Boolean,
      default: false,
    },
  },
  data: vm => ({
    fieldsetLegend: vm.$t('fields.engineering.cooling-efficiency.maximum-allowable-difference-measured-calculated'),
    diagnosticEmissionTimingLabel: vm.$t('fields.engineering.cooling-efficiency.timing-diagnostic-emission-exceeded-maximum-allowable-difference'),
    highValuesNumericOptions: { decimalPlaces: 0 },
  }),
  validations() {
    return {
      parameters: {
        maximumAllowableDifferenceTemperaturesOilMeasureCalculated: {
          valueToHigh: {
            required,
            minValue: minValue(0),
            maxValue: maxValue(40),
            isLessThanVeryHigh: this.isValueLessThanVeryHigh,
          },
          valueToVeryHigh: {
            required,
            minValue: minValue(0),
            maxValue: maxValue(40),
            isGreaterThanHigh: this.isValueGreaterThanHigh,
          },
        },
        timingDiagnosticEmissionMaximumPermissibleDifferenceExceeded: {
          required,
          minValue: minValue(0),
          maxValue: maxValue(300),
          isMultipleOfCycle: value => value % this.parameters?.algorithmExecutionCycleTime === 0,
        },
      },
    };
  },
  computed: {
    invalidData: {
      cache: false,
      get() {
        return this.$v.$invalid;
      },
    },
    inputOptions() {
      return {
        valueToHigh: {
          min: 0,
          max: 40,
          maxLength: 2,
          disabled: this.saving,
          state: this.$bsStateValidator(this.$v.parameters
            .maximumAllowableDifferenceTemperaturesOilMeasureCalculated.valueToHigh.$invalid),
        },
        valueToVeryHigh: {
          min: 0,
          max: 40,
          maxLength: 2,
          disabled: this.saving,
          state: this.$bsStateValidator(this.$v.parameters
            .maximumAllowableDifferenceTemperaturesOilMeasureCalculated.valueToVeryHigh.$invalid),
        },
        timingDiagnosticEmissionMaximumPermissibleDifferenceExceeded: {
          state: this.$bsStateValidator(this.$v.parameters
            .timingDiagnosticEmissionMaximumPermissibleDifferenceExceeded.$invalid),
        },
      };
    },
  },
  methods: {
    getValueToHighValidationMessage() {
      const { valueToHigh } = this.$v.parameters
        .maximumAllowableDifferenceTemperaturesOilMeasureCalculated;
      const {
        required: validRequired,
        minValue: validMinValue,
        maxValue: validMaxValue,
      } = valueToHigh;
      if (validRequired && validMinValue && validMaxValue) return '';
      return this.$t('messages.MA086.message', {
        minValue: 0,
        maxValue: 40,
      });
    },
    getValueToVeryHighValidationMessage() {
      const { valueToVeryHigh } = this.$v.parameters
        .maximumAllowableDifferenceTemperaturesOilMeasureCalculated;
      const {
        required: validRequired,
        minValue: validMinValue,
        maxValue: validMaxValue,
      } = valueToVeryHigh;
      if (validRequired && validMinValue && validMaxValue) return '';
      return this.$t('messages.MA086.message', {
        minValue: 0,
        maxValue: 40,
      });
    },
    getRangeValidationTimingDiagnosticEmission() {
      const timingDiagnosticEmission = this.$v.parameters
        .timingDiagnosticEmissionMaximumPermissibleDifferenceExceeded;
      const {
        required: validRequired,
        minValue: validMinValue,
        maxValue: validMaxValue,
      } = timingDiagnosticEmission;
      if (validRequired && validMinValue && validMaxValue) {
        return '';
      }
      return this.$t('messages.MA086.message', {
        minValue: 0,
        maxValue: 300,
      });
    },
    getMultipleValidationTimingDiagnosticEmission() {
      const timingDiagnosticEmission = this.$v.parameters
        .timingDiagnosticEmissionMaximumPermissibleDifferenceExceeded;
      if (!timingDiagnosticEmission.isMultipleOfCycle) {
        return this.$t('messages.MA089.message', {
          value: this.parameters?.algorithmExecutionCycleTime,
        });
      }
      return '';
    },
    validateHighValues() {
      const { valueToVeryHigh, valueToHigh } = this.$v.parameters
        .maximumAllowableDifferenceTemperaturesOilMeasureCalculated;
      if (valueToVeryHigh.isGreaterThanHigh && valueToHigh.isLessThanVeryHigh) return;
      this.$pushAlert({
        code: 'MA060',
        messageParams: {
          field1: this.$t('fields.engineering.cooling-efficiency.value-to-very-high'),
          operator: getOperatorLabel('<='),
          field2: this.$t('fields.engineering.cooling-efficiency.value-to-high'),
        },
      });
    },
    isValueLessThanVeryHigh(value) {
      const { valueToVeryHigh } = this.parameters
        ?.maximumAllowableDifferenceTemperaturesOilMeasureCalculated;
      return valueToVeryHigh == null || valueToVeryHigh > value;
    },
    isValueGreaterThanHigh(value) {
      const { valueToHigh } = this.parameters
        ?.maximumAllowableDifferenceTemperaturesOilMeasureCalculated;
      return valueToHigh == null || valueToHigh < value;
    },
  },
  watch: {
    invalidData() {
      this.$emit('form-status-update', this.invalidData);
    },
    editMode: {
      handler() {
        this.$v.$reset();
      },
      immediate: true,
    },
  },
};
</script>

<style lang="sass" scoped>
  @import "src/styles/defaults/variables"
  @import "src/styles/defaults/mobile-first"

  .parameters-form
    width: 100%
    padding: 0 0 15px
    border: 1px solid $color-border-gray
    border-radius: 3px
    &__title
      background-color: var(--color-background-dark)
      padding: 2px 10px
      font-weight: bold
    &__fieldset
      margin: 15px
    &__input
      +media-min-sm()
        width: 130px
    ::v-deep
      .label-edit_viewing
        opacity: .65
  .manage-fonts_16px
    .parameters-form
      &__input
        width: 140px
  .manage-fonts_18px
    .parameters-form
      &__input
        width: 160px
</style>
